/**
 * Whether the sheet is safe to walk away from.
 *
 * Uses the same `Badge` the report card editor uses for its save state
 * (`ReportFormHeader`) — amber "Saving…" going green "Saved" — so the two
 * editors report the same thing the same way. A teacher who has learned to
 * glance at one already knows how to read the other.
 *
 * Three states, not two. The report editor only ever needs saving/saved because
 * it saves on demand; a gradebook writes on every committed cell, so a failed
 * write is a real and silent way to lose a mark. That case gets the destructive
 * variant, a count, and a retry — it is the whole reason this component reads
 * the queue rather than a boolean.
 *
 * Fed by `useSyncQueue`; it renders that queue's summary and nothing else.
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function SyncIndicator({ status, onRetry, className }) {
  const { state, pending, failed } = status;

  if (state === "error") {
    return (
      <span className={cn("flex items-center gap-2", className)} role="status" aria-live="assertive">
        <Badge variant="destructive">
          {failed === 1 ? "1 mark didn't save" : `${failed} marks didn't save`}
        </Badge>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-destructive underline underline-offset-2 hover:no-underline"
          >
            Retry
          </button>
        ) : null}
      </span>
    );
  }

  if (state === "saving") {
    return (
      <span className={cn("flex items-center", className)} role="status" aria-live="polite">
        <Badge variant="warning">Saving…</Badge>
        {/* The count is deliberately not shown: it counts cells mid-flight and
            flickers as they settle, which reads as instability rather than
            progress. It stays in the DOM for screen readers. */}
        <span className="sr-only">{pending} pending</span>
      </span>
    );
  }

  return (
    <span className={cn("flex items-center", className)} role="status" aria-live="polite">
      <Badge variant="success">Saved</Badge>
    </span>
  );
}
